/**
 * Home — client-aware landing for the web PWA.
 *
 * Branches on the real session-derived view from roleView:
 *   - client-new        → on a brokerage's address (its platform subdomain or
 *                          its own custom domain) that brokerage's page — or
 *                          the agent's, on an agent's own domain — rendered in
 *                          place at `/` (useHostBrokerage, spec 011); anywhere
 *                          else ClientWelcome (no transaction yet / anonymous)
 *   - client-returning  → ClientHome     (transaction in progress)
 *
 * The view is computed once in the RoleViewProvider (mounted as the Shell
 * layout); the Shell nav reads from the same context so the two stay in sync.
 * While a client's dossier lookup resolves we hold a light placeholder rather
 * than flashing the welcome surface before flipping to the home.
 */

import { AgentLandingPage } from "../auth/AgentLandingPage";
import { BrokeragePage } from "../auth/BrokeragePage";
import { useRoleView } from "../../shell/roleView";
import { ClientHome } from "./ClientHome";
import { ClientOnboarding } from "./ClientOnboarding";
import { ClientWelcome } from "./ClientWelcome";
import { useHostBrokerageResult } from "./customDomain";

export function Home() {
  const { view, loading } = useRoleView();
  if (loading) return <HomeLoading />;
  if (view === "client-new") return <PublicLanding />;
  if (view === "client-onboarding") return <ClientOnboarding />;
  return <ClientHome />;
}

function PublicLanding() {
  const host = useHostBrokerageResult();
  // Hold the placeholder while a custom domain is looked up, so a brokerage's
  // visitor never sees the generic page flash before their broker's page.
  if (host.kind === "checking") return <HomeLoading />;
  if (host.kind === "brokerage") {
    return host.agentId ? (
      <AgentLandingPage slug={host.slug} agentId={host.agentId} inPlace />
    ) : (
      <BrokeragePage slug={host.slug} inPlace />
    );
  }
  return <ClientWelcome />;
}

function HomeLoading() {
  return (
    <div
      style={{
        minHeight: "40vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--rp-color-text-muted)",
      }}
    >
      Loading…
    </div>
  );
}
